import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-12 items-center justify-center rounded-md bg-vibebops-highlight p-2 text-vibebops-textMuted border-2 border-black shadow-inner relative overflow-hidden",
      className
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-2 text-sm font-bold relative overflow-hidden transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-2 border-black group mx-1.5",
      "data-[state=inactive]:bg-vibebops-card data-[state=inactive]:text-vibebops-textMuted hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-0.5 hover:translate-y-0.5",
      "data-[state=active]:bg-vibebops-accent data-[state=active]:text-white data-[state=active]:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]",
      className
    )}
    {...props}
  >
    {/* Circuit board pattern */}
    <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-300" 
      style={{ 
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none'/%3E%3Cpath d='M10 10h10v10H10zM30 10h10v10H30zM50 10h10v10H50zM70 10h10v10H70zM90 10h10v10H90zM10 30h10v10H10zM30 30h10v10H30zM50 30h10v10H50zM70 30h10v10H70zM90 30h10v10H90zM10 50h10v10H10zM30 50h10v10H30zM50 50h10v10H50zM70 50h10v10H70zM90 50h10v10H90zM10 70h10v10H10zM30 70h10v10H30zM50 70h10v10H50zM70 70h10v10H70zM90 70h10v10H90zM10 90h10v10H10zM30 90h10v10H30zM50 90h10v10H50zM70 90h10v10H70zM90 90h10v10H90z' stroke='%2366BBFF' stroke-opacity='1' stroke-width='1' fill='none'/%3E%3C/svg%3E")`,
        backgroundSize: '40px 40px'
      }}>
    </div>
    
    {/* Glowing accent border for active state */}
    <div className="absolute inset-0 p-[2px] overflow-hidden pointer-events-none opacity-0 data-[state=active]:opacity-100">
      <div className="absolute inset-0 transform rotate-0 group-hover:rotate-180 transition-transform duration-1000 ease-in-out">
        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-vibebops-accent2 to-transparent absolute top-0 left-0"></div>
        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-vibebops-accent to-transparent absolute bottom-0 left-0"></div>
        <div className="w-[1px] h-full bg-gradient-to-b from-transparent via-vibebops-accent2 to-transparent absolute left-0 top-0"></div>
        <div className="w-[1px] h-full bg-gradient-to-b from-transparent via-vibebops-accent to-transparent absolute right-0 top-0"></div>
      </div>
    </div>
    
    {/* Moving data dots for active state */}
    <div className="absolute inset-0 opacity-0 data-[state=active]:opacity-100 group-hover:opacity-100">
      <div className="absolute top-0 left-[10%] w-1 h-1 bg-vibebops-accent2 rounded-full animate-data-packet opacity-0 group-hover:opacity-100" style={{ '--path-x': '80%', '--path-y': '90%', animationDelay: '0s' } as React.CSSProperties}></div>
      <div className="absolute bottom-0 right-[10%] w-1 h-1 bg-vibebops-accent rounded-full animate-data-packet opacity-0 group-hover:opacity-100" style={{ '--path-x': '-80%', '--path-y': '-90%', animationDelay: '0.4s' } as React.CSSProperties}></div>
    </div>
    
    {/* Content text */}
    <span className="relative z-10">{props.children}</span>
  </TabsPrimitive.Trigger>
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
